/**
 * One client-side read of the org's object list (CRM Workspace 2027).
 *
 * `GET /api/crm/metadata/objects` builds the object bar, the field-group
 * order, the association cards and the related-records cards — and each of
 * those used to fetch it for itself, so one record page opened with four
 * requests for the same rows (measured on Maiteams HQ 2, 2026-09-05: four
 * per load, 0.9–1.1 s each on the dev server). Every caller wants a subset
 * of ONE superset — active objects, system ones included — so this module
 * makes that request once, shares it with everyone who asks while it is in
 * flight, keeps it for CRM_OBJECT_LIST_TTL_MS, and filters locally.
 *
 * The filters mirror the route's own query parameters exactly:
 *   - the route answers active objects only unless `include_inactive` is set,
 *     and no caller here sets it, so the superset is active-only too;
 *   - the route defaults `include_system=true`; its non-default `false`
 *     narrowing keeps rows where `is_system !== true`, and no caller asks for it;
 *   - `custom_only=true` is `is_custom === true`.
 *
 * A client-side write to `crm_object_metadata` goes through `patchCrmObject`
 * (or, for the shapes it does not cover, `notifyCrmObjectListChanged`), which
 * drops the cache — this module's copy and the SWR readers' — and dispatches
 * `crm:objects-changed` for the readers that keep their own copy. Those are
 * the only ways the list is forgotten, so a writer that only updates its own
 * local state leaves every other reader on the page showing the pre-save rows
 * for the rest of the TTL.
 * Nothing here is a hook: the callers that keep their own loading state keep
 * it, and the SWR readers pass `crmObjectListFetcher` as their fetcher so
 * their key shares the same request.
 */

import { mutate } from 'swr';
import { registerOrgSwitchReset } from '@/lib/org-switch/reset-registry';

export const CRM_OBJECTS_ENDPOINT = '/api/crm/metadata/objects';
export const CRM_OBJECT_LIST_URL = `${CRM_OBJECTS_ENDPOINT}?include_system=true`;
export const CRM_OBJECTS_CHANGED_EVENT = 'crm:objects-changed';
export const CRM_OBJECT_LIST_TTL_MS = 60_000;

export interface CrmObjectListRow {
  id: string;
  name_singular: string;
  name_plural: string;
  label_singular?: string | null;
  label_plural: string;
  icon?: string | null;
  is_custom?: boolean;
  is_active?: boolean;
  is_system?: boolean;
  settings?: Record<string, unknown> | null;
  parent_link_target?: string | null;
  [key: string]: unknown;
}

/** The route's body, kept whole so SWR readers see what they always saw. */
export interface CrmObjectListBody {
  success?: boolean;
  data?: { objects?: CrmObjectListRow[] };
}

export interface CrmObjectListFilter {
  /** Default true: the superset, which is what the bare route answers. False drops `is_system` objects — the route's non-default `include_system=false`. */
  includeSystem?: boolean;
  /** True keeps `is_custom` objects only, like `custom_only=true`. */
  customOnly?: boolean;
}

export class CrmObjectListError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Failed to fetch CRM objects (${status})`);
    this.name = 'CrmObjectListError';
    this.status = status;
  }
}

let cached: { body: CrmObjectListBody; at: number } | null = null;
let inflight: Promise<CrmObjectListBody> | null = null;
let listening = false;

/** Forget this module's copy alone. */
function forgetCachedBody(): void {
  cached = null;
  inflight = null;
}

/**
 * Forget the cached list — this module's copy AND the one SWR keeps — so the
 * next reader fetches again.
 *
 * The SWR half is not optional. `crmObjectListFetcher` shares this module's
 * request, but swr still stores the body it returned under
 * `CRM_OBJECT_LIST_URL`, and it keeps a dedupe marker for that key until
 * `dedupingInterval` (60s) after the response — while that marker exists, a
 * hook mounting on the key skips revalidation outright. So dropping only this
 * module's copy left the SWR readers (`useObjectCustomFields`, whose
 * `fieldGroupOrder` is the record panel's property-group order, and
 * `useWorkflowCustomProperties`) rendering the pre-save list for the rest of
 * that minute, whether they stayed mounted or remounted. A keyed global
 * `mutate` drops the marker and revalidates whoever is mounted; with no reader
 * mounted it drops the marker alone and fires no request of its own. It
 * reaches those hooks because this app mounts no `<SWRConfig provider>`, so
 * both sides are swr's default cache.
 */
export function invalidateCrmObjectList(): void {
  forgetCachedBody();
  void mutate(CRM_OBJECT_LIST_URL);
}

/**
 * Forget the cached list AND tell the readers that keep their own copy (the
 * CRM tab strip re-fetches on this event). Use it for the writes
 * `patchCrmObject` does not cover — creating an object, deleting one.
 */
export function notifyCrmObjectListChanged(): void {
  forgetCachedBody();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CRM_OBJECTS_CHANGED_EVENT));
  }
  // Last, and exactly once: an event listener that re-reads the list does so
  // through this module, so its request is the one the revalidating SWR
  // readers join. Mutating before the dispatch would have the listener-side
  // invalidation drop the dedupe marker a second time, and one save would
  // fetch the list twice.
  void mutate(CRM_OBJECT_LIST_URL);
}

/**
 * PATCH one object's metadata row, and drop the shared list once it lands.
 *
 * Every client-side PATCH of an object goes through here rather than
 * remembering the invalidation per call site: a save that only updates its
 * own component state (the association-card layout did) left the next reader
 * of the same object — the cards, `field_group_order`, the tab strip —
 * rendering the pre-save settings until the TTL expired.
 *
 * Returns the raw Response, so callers keep their own error handling. A
 * request that fails changes nothing, so it invalidates nothing.
 */
export async function patchCrmObject(
  objectId: string,
  patch: Record<string, unknown>
): Promise<Response> {
  const res = await fetch(`${CRM_OBJECTS_ENDPOINT}/${objectId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (res.ok) notifyCrmObjectListChanged();
  return res;
}

function ensureListener(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  // Module state only. `notifyCrmObjectListChanged` is the one dispatcher and
  // drops the SWR side itself, right after this listener has run — doing it
  // here as well would drop the dedupe marker a second time and make one save
  // fetch the list twice.
  window.addEventListener(CRM_OBJECTS_CHANGED_EVENT, forgetCachedBody);
}

async function request(): Promise<CrmObjectListBody> {
  const res = await fetch(CRM_OBJECT_LIST_URL);
  if (!res.ok) throw new CrmObjectListError(res.status);
  return (await res.json()) as CrmObjectListBody;
}

/**
 * The superset body: the cached one while it is fresh, the in-flight request
 * while one is running, otherwise a new request. A failed request caches
 * nothing, so the next reader tries again.
 */
export function loadCrmObjectListBody(): Promise<CrmObjectListBody> {
  ensureListener();
  if (cached && Date.now() - cached.at < CRM_OBJECT_LIST_TTL_MS) {
    return Promise.resolve(cached.body);
  }
  if (inflight) return inflight;
  const started = request().then(
    (body) => {
      // A later invalidation replaced `inflight`; do not resurrect a stale body.
      if (inflight === started) {
        cached = { body, at: Date.now() };
        inflight = null;
      }
      return body;
    },
    (error: unknown) => {
      if (inflight === started) inflight = null;
      throw error;
    }
  );
  inflight = started;
  return started;
}

/** SWR-shaped: ignores the key it is handed and answers the shared body. */
export function crmObjectListFetcher(): Promise<CrmObjectListBody> {
  return loadCrmObjectListBody();
}

export function filterCrmObjectList(
  rows: readonly CrmObjectListRow[],
  filter: CrmObjectListFilter = {}
): CrmObjectListRow[] {
  const includeSystem = filter.includeSystem ?? true;
  return rows.filter((row) => {
    if (!includeSystem && row.is_system === true) return false;
    if (filter.customOnly && row.is_custom !== true) return false;
    return true;
  });
}

/** The object rows a caller would have got from the route with these parameters. */
export async function fetchCrmObjectList(filter: CrmObjectListFilter = {}): Promise<CrmObjectListRow[]> {
  const body = await loadCrmObjectListBody();
  return filterCrmObjectList(body.data?.objects ?? [], filter);
}

// An in-place org switch keeps this module and swr's default cache alive: the
// object list and every SWR entry (custom fields, the object list's SWR
// readers) belong to the old org (lib/org-switch/reset-registry.ts).
registerOrgSwitchReset('crm-object-list', () => {
  forgetCachedBody();
});
registerOrgSwitchReset('swr-cache', async () => {
  // The default (revalidating) form, not revalidate:false: that keeps swr's
  // in-flight markers, so the new org's first reader would join the old org's
  // request inside the 60 s dedupe window and be left with no data. With no
  // reader mounted this sends no request.
  await mutate(() => true, undefined);
});
